import type { ReactNode } from 'react';

interface PageHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Right-side slot for action buttons / badges (rendered with white-on-gradient text by default). */
  trailing?: ReactNode;
  /** Left-side slot, typically a back button. */
  leading?: ReactNode;
  /** Full-width slot rendered below the title row (e.g. filter tabs). */
  children?: ReactNode;
  className?: string;
}

/**
 * Unified page header — gradient (hotel --theme-color-1 → --theme-color-2)
 * with white title + translucent subtitle. Used by every guest-app page so
 * the visual identity stays coherent across modules.
 */
export default function PageHeader({ title, subtitle, trailing, leading, children, className }: PageHeaderProps) {
  const hasFlexLayout = leading || trailing;
  return (
    <header className={`page-header${className ? ` ${className}` : ''}`}>
      {hasFlexLayout ? (
        <div className="d-flex justify-content-between align-items-start" style={{ gap: '0.75rem' }}>
          {leading && <div className="page-header-leading">{leading}</div>}
          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            <h2>{title}</h2>
            {subtitle && <p>{subtitle}</p>}
          </div>
          {trailing && <div className="page-header-trailing">{trailing}</div>}
        </div>
      ) : (
        <>
          <h2>{title}</h2>
          {subtitle && <p>{subtitle}</p>}
        </>
      )}
      {children}
    </header>
  );
}
